import PropTypes from 'prop-types';
import React, { createContext, useContext, useMemo } from 'react';

import useLiveData from 'hooks/useLiveData';
import { useNeighborhoodsQuery } from 'hooks/useNeighborhoodsQuery';

const NeighborhoodsContext = createContext();

export const useNeighborhoodsData = () => {
	const context = useContext(NeighborhoodsContext);
	if (!context) {
		throw new Error('useNeighborhoodsData must be used within a NeighborhoodsProvider');
	}
	return context;
}

const NeighborhoodsProvider = ({ children, location, staticData }) => {

	const { data } = useNeighborhoodsQuery();
	const effectiveStaticData = React.useMemo(() => staticData || data || {}, [staticData, data]);
	const neighborhoodsEdges = effectiveStaticData?.allBeachNeighborhood?.edges || [];
	const neighborhoodsNodes = neighborhoodsEdges.map(({ node }) => node);
	const neighborhoodsKeys = neighborhoodsNodes.length > 0 ? Object.keys(neighborhoodsNodes[0]) : [];
	const neighborhoodIds = neighborhoodsNodes.length > 0 ? neighborhoodsNodes.map(({ id }) => id) : [];
	const relationKeys = neighborhoodsKeys.filter((key) => key[0] === key[0].toUpperCase());

	const currentNeighborhoodStatic = neighborhoodsNodes.find(
		(n) => n.uri === location?.pathname
	);

	const {
		statusText: neighborhoodsStatusText,
		status: neighborhoodsStatus,
		response: neighborhoodsResponse,
		visibilityChange: neighborhoodsVisibilityChange,
	} = useLiveData({
		type: 'BeachNeighborhood',
		apiParams: {
			approved: true,
			deleted: false,
			archived: false,
			_sort: 'sort',
			_order: 'asc',
			// Only include neighborhood IDs if there are fewer than 16 to avoid extrenuous id lookups
			// At the time of this implementation, there's only 16 beach neighborhoods
			...(neighborhoodIds.length > 0 && neighborhoodIds.length < 16 ? { id: neighborhoodIds.join(',') } : {}),
			...(neighborhoodsKeys.length > 0 ? { _include: neighborhoodsKeys.join(',') } : {}),
			...(relationKeys.length > 0 ? { _join: relationKeys.join(',') } : {}),
		},
		staticData: effectiveStaticData,
	});

	const liveNeighborhoods = neighborhoodsResponse?.data;
	let neighborhoods = neighborhoodsNodes;
	if (Array.isArray(liveNeighborhoods) && liveNeighborhoods.length > 0 && neighborhoodsStatusText === 'LOADED') {
		neighborhoods = liveNeighborhoods;
	}
	if (Array.isArray(liveNeighborhoods) && neighborhoodsVisibilityChange) neighborhoods = liveNeighborhoods;

	let currentNeighborhood = neighborhoods.find((nei) => nei.uri === location?.pathname) || currentNeighborhoodStatic;
	const searchParams = new URLSearchParams(location?.search);
	const neighborhoodPermalink = searchParams.get('neighborhood');
	if (neighborhoodPermalink) {
		const neighborhoodFromSearch = neighborhoods.find((nei) => nei.permalink === neighborhoodPermalink);
		if (neighborhoodFromSearch) currentNeighborhood = neighborhoodFromSearch;
	}

	const isLoaded = neighborhoodsStatus >= 2 || !!currentNeighborhoodStatic;

	const contextValue = useMemo(() => ({
		currentNeighborhood,
		neighborhoods,
		isLoaded,
		staticData: effectiveStaticData,
		location,
	}), [currentNeighborhood, neighborhoods, isLoaded, effectiveStaticData, location]);

	return (
		<NeighborhoodsContext.Provider value={contextValue}>
			{children}
		</NeighborhoodsContext.Provider>
	);
}

export default NeighborhoodsProvider;

NeighborhoodsProvider.propTypes = {
	children: PropTypes.node.isRequired,
	location: PropTypes.shape({
		pathname: PropTypes.string,
	}),
	staticData: PropTypes.object,
};